interface GroupedTriangle {
  groupId?: number;
}

interface PreviewCacheEntry<TTriangle extends GroupedTriangle> {
  triangles: TTriangle[];
  triangleCount: number;
  vertexCount: number;
  parts?: any;
  analysis?: any;
}

/** Store returned full/group mesh responses as reusable per-group cache entries. */
export function mergePreviewResults<TTriangle extends GroupedTriangle, TEntry extends PreviewCacheEntry<TTriangle>>(
  cache: Map<number, TEntry>,
  results: readonly TEntry[],
): void {
  for (const result of results) {
    const groupIds = new Set<number>();
    for (const triangle of result.triangles || []) groupIds.add(triangle.groupId ?? 0);
    if (groupIds.size === 1) {
      cache.set(groupIds.values().next().value!, result);
      continue;
    }
    for (const groupId of groupIds) {
      const triangles = result.triangles.filter((triangle) => (triangle.groupId ?? 0) === groupId);
      cache.set(groupId, {
        ...result,
        triangles,
        triangleCount: triangles.length,
        vertexCount: triangles.length * 3,
      } as TEntry);
    }
  }
}

/** Rebuild the complete triangle payload from cached group responses. */
export function combinePreviewCache<TTriangle extends GroupedTriangle, TEntry extends PreviewCacheEntry<TTriangle>>(
  cache: Map<number, TEntry>,
): TEntry {
  const triangles: TTriangle[] = [];
  let triangleCount = 0;
  let parts: TEntry['parts'] = undefined;
  let analysis: TEntry['analysis'] = undefined;
  for (const entry of cache.values()) {
    // Do not spread a large triangle array into push(): dense single-part
    // previews can contain hundreds of thousands of triangles and exceed the
    // JavaScript call stack even though there is only one mesh part.
    if (entry.triangles?.length) {
      for (const triangle of entry.triangles) triangles.push(triangle);
    }
    triangleCount += entry.triangleCount || entry.triangles?.length || 0;
    if (entry.parts) parts = entry.parts;
    if (entry.analysis) analysis = entry.analysis;
  }
  return {
    triangles,
    triangleCount,
    vertexCount: triangleCount * 3,
    parts,
    analysis,
  } as TEntry;
}
